'use client';

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/translation';

export type FaqItem = { question: string; answer: string };

type LandingFaqAccordionProps = {
  items: FaqItem[];
  layout: 'twoColumn' | 'singleColumn';
  enableShowMore: boolean;
  titleKeyPrefix: 'landing.faq' | 'landing.faqPage';
  idPrefix: string;
};

export function LandingFaqAccordion({
  items,
  layout,
  enableShowMore,
  titleKeyPrefix,
  idPrefix,
}: LandingFaqAccordionProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(4);
  const [desktopVisibleCount, setDesktopVisibleCount] = useState(4);

  if (!items || items.length === 0) {
    return null;
  }

  const renderFaqItem = (item: FaqItem, index: number, idScope: string) => {
    const isOpen = openIndex === index;
    const headerId = `${idPrefix}-${idScope}-header-${index}`;
    const panelId = `${idPrefix}-${idScope}-panel-${index}`;

    const textAlignClasses = isRTL ? 'text-right' : 'text-left';

    return (
      <div
        key={`${idPrefix}-${index}-${item.question}`}
        className={cn(
          'overflow-hidden rounded-xl border border-emerald-100 bg-white/90 shadow-[0_1px_3px_rgba(15,118,110,0.06)] dark:border-emerald-900 dark:bg-gray-950/80',
          textAlignClasses,
          !isRTL && 'ltr',
        )}
      >
        <button
          type="button"
          id={headerId}
          className={cn(
            'flex min-h-[44px] cursor-pointer w-full items-center justify-between gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 sm:gap-4 sm:px-5',
            textAlignClasses,
          )}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenIndex(isOpen ? null : index)}
        >
          <span
            className={cn(
              'min-w-0 flex-1 text-sm font-medium text-foreground md:text-base',
              textAlignClasses,
            )}
          >
            {item.question}
          </span>
          <span
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-emerald-200 text-emerald-700 transition-transform dark:border-emerald-800 dark:text-emerald-300 sm:h-8 sm:w-8',
              isOpen ? 'rotate-180 bg-emerald-50/80 dark:bg-emerald-950/70' : 'bg-white/80 dark:bg-gray-900/80',
            )}
            aria-hidden="true"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </span>
        </button>
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className={cn(
            'grid overflow-hidden px-4 pb-0 text-sm text-muted-foreground transition-all duration-200 ease-out sm:px-5',
            textAlignClasses,
            isOpen ? 'grid-rows-[1fr] pb-4' : 'grid-rows-[0fr]',
          )}
        >
          <div
            className={cn(
              'overflow-hidden text-pretty leading-relaxed [overflow-wrap:anywhere]',
              textAlignClasses,
              !isRTL && 'ltr',
            )}
          >
            {item.answer}
          </div>
        </div>
      </div>
    );
  };

  const mobileMaxItems = Math.min(10, items.length);
  const mobileVisibleItems = items.slice(0, Math.min(mobileVisibleCount, mobileMaxItems));
  const canShowMoreOnMobile = enableShowMore && mobileVisibleCount < mobileMaxItems;
  const desktopMaxItems = Math.min(10, items.length);
  const desktopVisibleItems = items
    .slice(0, Math.min(desktopVisibleCount, desktopMaxItems))
    .map((item, index) => ({ item, index }));
  const desktopLeftColumnItems = desktopVisibleItems.filter(({ index }) => index % 2 === 0);
  const desktopRightColumnItems = desktopVisibleItems.filter(({ index }) => index % 2 === 1);
  const canShowMoreOnDesktop = enableShowMore && desktopVisibleCount < desktopMaxItems;

  if (layout === 'singleColumn') {
    return (
      <div className="space-y-3">
        <div className="mb-8 text-center sm:mb-10">
          <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t(`${titleKeyPrefix}.titlePrefix`)}
            <span className="inline-block text-foreground">{t(`${titleKeyPrefix}.titleHighlight`)}</span>
            {t(`${titleKeyPrefix}.titleSuffix`)}
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((item, index) => renderFaqItem(item, index, 'single'))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="mb-8 text-center sm:mb-10">
        <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {t(`${titleKeyPrefix}.titlePrefix`)}
          <span className="inline-block text-foreground">{t(`${titleKeyPrefix}.titleHighlight`)}</span>
          {t(`${titleKeyPrefix}.titleSuffix`)}
        </h2>
      </div>
      <div className="space-y-3 md:hidden">
        {mobileVisibleItems.map((item, index) => renderFaqItem(item, index, 'mobile'))}
        {canShowMoreOnMobile && (
          <motion.div
            className="flex justify-center pt-2"
            animate={{ y: [0, 8, 0], opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <button
              type="button"
              onClick={() => setMobileVisibleCount((prev) => Math.min(prev + 2, mobileMaxItems))}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border-2 border-emerald-300/90 bg-white/90 text-emerald-700 shadow-[0_6px_14px_rgba(16,185,129,0.18)] dark:border-emerald-700 dark:bg-gray-900/80 dark:text-emerald-300"
              aria-label={t('landing.faq.showMore', 'Show more questions')}
            >
              <Plus className="h-6 w-6" strokeWidth={2.5} />
            </button>
          </motion.div>
        )}
      </div>
      <div className="hidden gap-3 md:grid md:grid-cols-2 md:items-start">
        <div className="space-y-3">
          {desktopLeftColumnItems.map(({ item, index }) => renderFaqItem(item, index, 'desktop'))}
        </div>
        <div className="space-y-3">
          {desktopRightColumnItems.map(({ item, index }) => renderFaqItem(item, index, 'desktop'))}
        </div>
      </div>
      {canShowMoreOnDesktop && (
        <motion.div
          className="hidden justify-center pt-4 md:flex"
          animate={{ y: [0, 8, 0], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <button
            type="button"
            onClick={() => setDesktopVisibleCount((prev) => Math.min(prev + 2, desktopMaxItems))}
            className="inline-flex h-14 w-14 cursor-default items-center justify-center rounded-full border-2 border-emerald-300/90 bg-white/90 text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,0.2)] md:cursor-pointer dark:border-emerald-700 dark:bg-gray-900/80 dark:text-emerald-300"
            aria-label={t('landing.faq.showMore', 'Show more questions')}
          >
            <Plus className="h-8 w-8" strokeWidth={2.8} />
          </button>
        </motion.div>
      )}
    </div>
  );
}

export function LandingFaq() {
  const { t } = useTranslation();
  const items = t('landing.faq.items', {
    returnObjects: true,
  }) as Array<{ question: string; answer: string }>;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <LandingFaqAccordion
      items={items}
      layout="twoColumn"
      enableShowMore
      titleKeyPrefix="landing.faq"
      idPrefix="landing-faq"
    />
  );
}
